import { Colors, Icon, Text } from '@amelio/ui-library';

import { recapOutcome } from '../recapOutcome';
import { RecapStatusZone } from './RecapStatusZone';

import type { MeetingRecapRetry, MeetingTranscriptLabels } from '../types';
import type { MeetingRecap } from '@/types/review/PostReviewMeeting';

import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';

interface DownloadLinkProps {
    label: string;
    onClick: () => void;
    dataTestId: string;
}

const DownloadLink = ({ label, onClick, dataTestId }: DownloadLinkProps) => {
    return (
        <button
            type={'button'}
            onClick={onClick}
            className={
                'text-primary hover:text-primary-700 flex items-center gap-2 text-sm underline focus:outline-none'
            }
            data-testid={dataTestId}
        >
            <Icon
                name={'arrow-import-2'}
                size={'sm'}
                color={Colors.PRIMARY}
            />
            {label}
        </button>
    );
};

interface RecapPanelProps {
    labels: MeetingTranscriptLabels;
    recap: MeetingRecap | null;
    isBusy: boolean;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    onImportAnother: () => void;
    onFileSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
    recapRetry: MeetingRecapRetry;
    onDownloadTranscript?: () => void;
    onDownloadRecap?: () => void;
    acceptedFileTypes: string;
    disabled?: boolean;
}

/** What is read once a transcript exists: the recap, the way to replace the transcript, and what
 * the journey lets the reader take away. */
export const RecapPanel = ({
    labels,
    recap,
    isBusy,
    fileInputRef,
    onImportAnother,
    onFileSelected,
    recapRetry,
    onDownloadTranscript,
    onDownloadRecap,
    acceptedFileTypes,
    disabled = false,
}: RecapPanelProps) => {
    const planLock = usePlanLock();
    return (
        <div className={'flex w-full items-end justify-end p-6'}>
            <div
                className={'w-full rounded-xl bg-white/80 px-6 py-5'}
                data-testid={'meeting-transcript-summary'}
            >
                <div className={'mb-4 flex items-center justify-between'}>
                    <Text
                        size={'default'}
                        weight={'semibold'}
                        color={Colors.BASE_600}
                    >
                        {labels.recapTitle}
                    </Text>

                    <button
                        type={'button'}
                        onClick={planLock.isLocked ? planLock.onLocked : onImportAnother}
                        disabled={!planLock.isLocked && disabled}
                        className={
                            'text-primary hover:text-primary-700 text-sm underline focus:outline-none disabled:opacity-50'
                        }
                        data-testid={'import-another-transcript-button'}
                    >
                        {planLock.isLocked && <PlanLockIcon className={'mr-1 inline'} />}
                        {labels.importAnother}
                    </button>
                </div>

                {/* The AI is named only beside what it wrote, never above a wait, an unavailable recap or
                    a file it could write nothing from. */}
                {recap && recapOutcome(recap) !== 'unusable' && (
                    <div className={'mb-4 flex items-center gap-2'}>
                        <Icon
                            name={'lucide-sparkles'}
                            size={'sm'}
                            color={Colors.SECONDARY}
                        />
                        <Text
                            size={'small'}
                            color={Colors.SECONDARY}
                        >
                            {labels.generatedBy}
                        </Text>
                    </div>
                )}

                <RecapStatusZone
                    recap={recap}
                    isWriting={isBusy && !recap}
                    retry={recapRetry}
                    labels={labels}
                    disabled={disabled}
                />

                {/* Two buttons, each named by what it hands back, never one that changes meaning.
                    A journey offering only one passes only that one. */}
                <div className={'mt-4 flex flex-wrap items-center gap-5'}>
                    {onDownloadTranscript && labels.downloadTranscript && (
                        <DownloadLink
                            label={labels.downloadTranscript}
                            onClick={onDownloadTranscript}
                            dataTestId={'download-transcript-button'}
                        />
                    )}

                    {onDownloadRecap && labels.downloadRecap && (
                        <DownloadLink
                            label={labels.downloadRecap}
                            onClick={onDownloadRecap}
                            dataTestId={'download-recap-button'}
                        />
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type={'file'}
                    accept={acceptedFileTypes}
                    disabled={disabled}
                    className={'hidden'}
                    onChange={onFileSelected}
                />
            </div>
        </div>
    );
};
